import { apiClient } from '@/lib/api/client'
import type { PaymentTermsTemplateDto, PaymentTermsStepDto } from '@/types'

export type TranslationDto = {
  language: string
  label: string
}

export type ProjectTypeSettingDto = {
  id: string
  code: string
  icon: string | null
  sortOrder: number
  isEnabled: boolean
  translations: TranslationDto[]
}

export type CreateProjectTypeRequest = {
  icon: string | null
  translations: TranslationDto[]
}

export type UpdateProjectTypeRequest = {
  icon: string | null
  isEnabled: boolean
  translations: TranslationDto[]
}

export type ReorderItem = {
  id: string
  sortOrder: number
}

export type OrgLanguagesDto = {
  supportedLanguages: string[]
}

export async function getSettingsProjectTypes(): Promise<ProjectTypeSettingDto[]> {
  return apiClient.get<ProjectTypeSettingDto[]>('/api/v2/settings/project-types')
}

export async function createSettingsProjectType(
  data: CreateProjectTypeRequest
): Promise<ProjectTypeSettingDto> {
  return apiClient.post<ProjectTypeSettingDto>('/api/v2/settings/project-types', data)
}

export async function updateSettingsProjectType(
  id: string,
  data: UpdateProjectTypeRequest
): Promise<ProjectTypeSettingDto> {
  return apiClient.put<ProjectTypeSettingDto>(`/api/v2/settings/project-types/${id}`, data)
}

export async function deleteSettingsProjectType(id: string): Promise<void> {
  return apiClient.delete<void>(`/api/v2/settings/project-types/${id}`)
}

export async function reorderSettingsProjectTypes(items: ReorderItem[]): Promise<void> {
  return apiClient.put<void>('/api/v2/settings/project-types/reorder', { items })
}

export async function getOrgLanguages(): Promise<OrgLanguagesDto> {
  return apiClient.get<OrgLanguagesDto>('/api/v2/settings/languages')
}

export async function updateOrgLanguages(supportedLanguages: string[]): Promise<OrgLanguagesDto> {
  return apiClient.put<OrgLanguagesDto>('/api/v2/settings/languages', { supportedLanguages })
}

// ── Payment Terms ─────────────────────────────────────────────────────────────

export async function getPaymentTermsTemplate(): Promise<PaymentTermsTemplateDto> {
  return apiClient.get<PaymentTermsTemplateDto>('/api/v2/settings/payment-terms')
}

export async function updatePaymentTermsTemplate(
  steps: Omit<PaymentTermsStepDto, 'id'>[],
  gracePeriodDays?: number,
  paymentTaskTolerancePct?: number
): Promise<PaymentTermsTemplateDto> {
  return apiClient.put<PaymentTermsTemplateDto>('/api/v2/settings/payment-terms', { steps, gracePeriodDays, paymentTaskTolerancePct })
}

export async function reorderPaymentTermsSteps(
  items: { id: string; sortOrder: number }[]
): Promise<void> {
  return apiClient.put<void>('/api/v2/settings/payment-terms/reorder', { items })
}

// ── Analytics Goals — DASH-006 + DASH-007 ────────────────────────────────────

export interface AnalyticsGoalsDto {
  monthlyRevenueTarget: number | null
  monthlySalesTarget: number | null
}

export async function getAnalyticsGoals(): Promise<AnalyticsGoalsDto> {
  return apiClient.get<AnalyticsGoalsDto>('/api/v2/settings/analytics-goals')
}

export async function updateAnalyticsGoals(data: {
  monthlyRevenueTarget: number | null
  monthlySalesTarget: number | null
}): Promise<AnalyticsGoalsDto> {
  return apiClient.put<AnalyticsGoalsDto>('/api/v2/settings/analytics-goals', data)
}

// ── Sales categories (SOLY-IMPORT-003 S07a) ──────────────────────────────────

/** Translations keyed by language ("nl", "en"); the label is printed when none matches. */
export type CategorySettingDto = {
  id: string
  label: string
  sortOrder: number
  isHidden: boolean
  translations: Record<string, string>
}

export type CreateCategoryRequest = {
  label: string
  translations: Record<string, string>
}

export type UpdateCategoryRequest = {
  label: string
  isHidden: boolean
  translations: Record<string, string>
}

export async function getSettingsCategories(): Promise<CategorySettingDto[]> {
  return apiClient.get<CategorySettingDto[]>('/api/v2/settings/categories')
}

export async function createSettingsCategory(data: CreateCategoryRequest): Promise<CategorySettingDto> {
  return apiClient.post<CategorySettingDto>('/api/v2/settings/categories', data)
}

export async function updateSettingsCategory(id: string, data: UpdateCategoryRequest): Promise<CategorySettingDto> {
  return apiClient.put<CategorySettingDto>(`/api/v2/settings/categories/${id}`, data)
}

/** The whole list in its new order: the server refuses a partial one. */
export async function reorderSettingsCategories(ids: string[]): Promise<void> {
  return apiClient.put<void>('/api/v2/settings/categories/order', { ids })
}

// ── Winner group → category mappings (SOLY-IMPORT-003 S08) ───────────────────

/** Mirrors `ImportCategoryMappingSettingsController.MappingDto`. */
export type ImportCategoryMappingDto = {
  id: string
  sourceSystem: string
  groupNumber: string
  groupName: string | null
  categoryId: string
  categoryLabel: string
  categoryHidden: boolean
  createdAt: string
  updatedAt: string | null
}

export async function getImportCategoryMappings(): Promise<ImportCategoryMappingDto[]> {
  return apiClient.get<ImportCategoryMappingDto[]>('/api/v2/settings/import-category-mappings')
}

/** For the imports to come; lines already imported keep their category. */
export async function retargetImportCategoryMapping(id: string, categoryId: string): Promise<ImportCategoryMappingDto> {
  return apiClient.put<ImportCategoryMappingDto>(`/api/v2/settings/import-category-mappings/${id}`, { categoryId })
}

// ── Winner catalogue → supply mappings (SOLY-IMPORT-003 S12) ─────────────────

/** Mirrors `ImportSupplyMappingSettingsController.MappingDto`. */
export type ImportSupplyMappingDto = {
  id: string
  sourceSystem: string
  catalogKey: string
  catalogName: string | null
  mode: 'buy' | 'make' | 'none'
  supplierId: string | null
  supplierName: string | null
  createdAt: string
  updatedAt: string | null
}

export async function getImportSupplyMappings(): Promise<ImportSupplyMappingDto[]> {
  return apiClient.get<ImportSupplyMappingDto[]>('/api/v2/settings/import-supply-mappings')
}

/** For the imports to come; lines already imported keep their supply. */
export async function changeImportSupplyMapping(
  id: string,
  body: { mode: 'buy' | 'make' | 'none'; supplierId?: string | null },
): Promise<ImportSupplyMappingDto> {
  return apiClient.put<ImportSupplyMappingDto>(`/api/v2/settings/import-supply-mappings/${id}`, body)
}

/** The suppliers a catalogue can be bought from, readable with settings:manage alone. */
export async function getImportSupplyMappingSuppliers(): Promise<{ id: string; name: string }[]> {
  return apiClient.get<{ id: string; name: string }[]>('/api/v2/settings/import-supply-mappings/suppliers')
}
